/**
 * Notice Corporate preset → ControllerAppointmentForm initial-data (category B09001)
 */

import {
  flattenNoticeControllerPresetSource,
  buildAlternateAddressFields,
  buildPrimaryAddressFields,
  PRESET_MANUAL_CONTROLLER_CONTACT_CD,
  splitForeignAddress,
} from '../../notice-individual/composables/adaptControllerEfilingFormData';
import { mergeOfficerContactDataForPreset } from '../../../appoint-director/composables/adaptOfficerAppointmentEfilingFormData';
import { parseManualAddressText } from '@/features/startTask/utils/manualOfficerContactData';
import { parseLegacyContactPhone } from './formOptions';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function normalizeDateForEfiling(value: unknown): string {
  const raw = trim(value);
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.substring(0, 10);
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return raw;
}

export function isNoticeCorporateFlatFormData(fd: Record<string, unknown> | null | undefined): boolean {
  if (!fd || typeof fd !== 'object') return false;
  if (fd.formFields && typeof fd.formFields === 'object') return false;
  if (fd.saveController) return false;
  if (trim(fd.controller_full_name) || trim(fd.controllerFullName)) return false;
  if (trim(fd.controller_corporate_name) || trim(fd.controllerCorporateName)) return true;
  if (trim(fd.categoryCd) === 'B09001' || trim(fd.categoryCode) === 'B09001') return true;
  return false;
}

export function parseEfilingFormDataColumn(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

export function buildNoticeCorporateContactDataFromPreset(
  src: Record<string, unknown>,
): Record<string, unknown> {
  const safe = flattenNoticeControllerPresetSource(src);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = safe[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const office = pick('controller_registered_office', 'controllerRegisteredOffice');
  const parsedOffice = parseManualAddressText(office);
  const foreignFallback = splitForeignAddress(office);
  const altOffice = pick('controller_alternate_address', 'controllerAlternateAddress');
  const parsedAltOffice = altOffice ? parseManualAddressText(altOffice) : null;
  const countryCd = pick('controller_country_cd', 'controllerCountryCd', 'countryCd', 'COUNTRY_CD');
  const areaCode = pick('controller_area_code', 'controllerAreaCode', 'areaCode', 'AREA_CODE');
  let mobileNo = pick('controller_mobile_no', 'controllerMobileNo', 'mobileNo', 'MOBILE_NO');
  if (!mobileNo) {
    const legacy = parseLegacyContactPhone(pick('controller_contact_no', 'controllerContactNo'));
    if (!areaCode && legacy.areaCode) {
      return buildNoticeCorporateContactDataFromPreset({
        ...src,
        controller_area_code: legacy.areaCode,
        controller_mobile_no: legacy.mobileNo,
        controller_country_cd: legacy.countryCd || countryCd,
      });
    }
    mobileNo = legacy.mobileNo;
  }

  const jurisdictionCd = pick('controller_jurisdiction_cd', 'controllerJurisdictionCd', 'jurisdictionCd');
  const incorporationStatute = pick(
    'controller_incorporation_statute',
    'controllerIncorporationStatute',
    'incorporationStatute',
  ) || pick('controller_jurisdiction_statute', 'controllerJurisdictionStatute');
  const contactRegNo = pick(
    'controller_registration_no',
    'controllerRegistrationNo',
    'contactRegNo',
    'CONTACT_REG_NO',
    'nonAcrAuen',
  );
  const uen = pick('controller_uen', 'controllerUen', 'acrAuen', 'regNo');
  const became = normalizeDateForEfiling(pick('controller_became_date', 'controllerBecameDate'));
  const corporateName = pick('controller_corporate_name', 'controllerCorporateName');
  const contactCd = pick('contactCd', 'contact_cd', 'controllerCd');
  const email = pick('controller_email', 'controllerEmail');
  const legalForm = pick('controller_legal_form', 'controllerLegalForm', 'legalForm');
  const registerName = pick('controller_register_name', 'controllerRegisterName', 'corporateEntityRegister');

  const officeFields = (() => {
    if (parsedOffice.typeCd === 'B07001') {
      return buildPrimaryAddressFields(parsedOffice);
    }
    return {
      ...buildPrimaryAddressFields({
        typeCd: 'B07002',
        postalCode: '',
        blockNo: '',
        streetName: '',
        buildingName: '',
        addrLevel: '',
        addrUnit: '',
        foreignAddressLine1: parsedOffice.foreignAddressLine1 || foreignFallback.line1,
        foreignAddressLine2: parsedOffice.foreignAddressLine2 || foreignFallback.line2,
      }),
    };
  })();
  const alternateOfficeFields = parsedAltOffice ? buildAlternateAddressFields(parsedAltOffice) : {};

  return {
    contactCd,
    CONTACT_CD: contactCd,
    categoryCd: 'B09001',
    CATEGORY_CD: 'B09001',
    contactName: corporateName,
    CONTACT_NAME: corporateName,
    uen,
    UEN: uen,
    contactRegNo,
    CONTACT_REG_NO: contactRegNo,
    legalForm,
    LEGAL_FORM: legalForm,
    jurisdiction: jurisdictionCd,
    JURISDICTION: jurisdictionCd,
    incorporationStatute,
    INCORPORATION_STATUTE: incorporationStatute,
    corporateEntityRegister: registerName,
    CORPORATE_ENTITY_REGISTER: registerName,
    contactEmail: email,
    CONTACT_EMAIL: email,
    countryCd,
    COUNTRY_CD: countryCd,
    areaCode,
    AREA_CODE: areaCode,
    mobileNo,
    MOBILE_NO: mobileNo,
    addressTypeCd: office ? parsedOffice.typeCd : '',
    ADDRESS_TYPE_CD: office ? parsedOffice.typeCd : '',
    ...officeFields,
    ...alternateOfficeFields,
    dateOfBecoming: became,
    DATE_OF_BECOMING: became,
    confirmationByController: 'No',
    CONFIRMATION_BY_CONTROLLER: 'No',
  };
}

export function adaptNoticeCorporateToControllerAppointmentFormData(
  src: Record<string, unknown> | null | undefined,
  entityCdFallback = '',
  entityNameFallback = '',
): Record<string, unknown> {
  const safe = flattenNoticeControllerPresetSource(src);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = (safe as Record<string, unknown>)[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const entity = pick('entity', 'entityCd', 'icpCd') || entityCdFallback;
  const entityUen = pick(
    'entity_uen',
    'entityUEN',
    'EntityUEN',
    'company_registration_no',
    'companyRegistrationNo',
    'entityRegNo',
  );
  const contactCd = pick('contactCd', 'contact_cd');
  const corporateName = pick('controller_corporate_name', 'controllerCorporateName');
  const became = normalizeDateForEfiling(pick('controller_became_date', 'controllerBecameDate'));
  const jurisdictionCd = pick('controller_jurisdiction_cd', 'controllerJurisdictionCd', 'jurisdictionCd');
  const incorporationStatute = pick(
    'controller_incorporation_statute',
    'controllerIncorporationStatute',
    'incorporationStatute',
  ) || pick('controller_jurisdiction_statute', 'controllerJurisdictionStatute');
  const existingContact =
    safe.contactData && typeof safe.contactData === 'object'
      ? (safe.contactData as Record<string, unknown>)
      : undefined;
  const builtContact = buildNoticeCorporateContactDataFromPreset(safe);
  const contactData = mergeOfficerContactDataForPreset(builtContact, existingContact, contactCd);
  const presetControllerContactOption = corporateName
    ? {
      title: corporateName,
      value: contactCd || PRESET_MANUAL_CONTROLLER_CONTACT_CD,
    }
    : undefined;

  return {
    ...safe,
    entity,
    entityCd: entity,
    entityUEN: entityUen,
    entity_uen: entityUen,
    company_registration_no: entityUen,
    entityRegNo: entityUen,
    categoryCd: 'B09001',
    categoryCode: 'B09001',
    controller_corporate_name: corporateName,
    controllerCorporateName: corporateName,
    contactCd,
    contact_cd: contactCd,
    controllerCd: contactCd,
    controller_became_date: became,
    dateOfBecoming: became,
    dateOfAppointed: { value: became },
    acrAuen: pick('controller_uen', 'controllerUen'),
    legalForm: pick('controller_legal_form', 'controllerLegalForm'),
    jurisdiction: jurisdictionCd,
    jurisdictionCd,
    incorporationStatute,
    corporateEntityRegister: pick('controller_register_name', 'controllerRegisterName'),
    nonAcrAuen: pick('controller_registration_no', 'controllerRegistrationNo'),
    countryCode: pick('controller_country_cd', 'controllerCountryCd', 'countryCd', 'COUNTRY_CD'),
    areaCode: pick('controller_area_code', 'controllerAreaCode', 'areaCode', 'AREA_CODE'),
    mobileNo: pick('controller_mobile_no', 'controllerMobileNo', 'mobileNo', 'MOBILE_NO'),
    controller_country_cd: pick('controller_country_cd', 'controllerCountryCd'),
    controller_area_code: pick('controller_area_code', 'controllerAreaCode'),
    controller_mobile_no: pick('controller_mobile_no', 'controllerMobileNo'),
    controller_incorporation_statute: incorporationStatute,
    contactData,
    presetControllerContactOption,
    entityName: { value: pick('company_name', 'companyName', 'entityName') || entityNameFallback },
  };
}
